import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { 
  getAuth, 
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { CircularProgress, Box } from '@mui/material';
import tokenManager from './TokenManager';
import { districtCache } from '../services/districtCacheService';
import mailchimpService from '../services/mailChimpService';
import apiClient from '../utils/apiClient';
import { SUSPENDED_ACCOUNT_MESSAGE } from '../utils/firebaseErrorMessages';
import { normalizeProducts, clearActiveProduct } from '../constants/productAccess';

const AuthContext = createContext({
  user: null,
  loading: true,
  login: async () => {},
  logout: async () => {},
  resetPassword: async () => {}
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const auth = getAuth();
  const db = getFirestore();

  const handleSessionExpire = useCallback(async () => {
    tokenManager.clearSessionTimers();
    try {
      try { await apiClient.post('/logout'); } catch (_) {}
      await signOut(auth);
      clearActiveProduct();
      try { await districtCache.clearAllCache(); } catch (_) {}
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (!['analyticsConsent', 'hasExplicitConsent'].includes(key)) {
          localStorage.removeItem(key);
        }
      }
    } catch (_) {}
    setUser(null);
  }, [auth]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (firebaseUser) {
          // Get initial token and decode claims
          const token = await firebaseUser.getIdToken();
          const decodedToken = JSON.parse(atob(token.split('.')[1]));
          
          // Get Firestore profile data
          const userProfileRef = doc(db, 'user_profiles', firebaseUser.uid);
          const userProfileSnap = await getDoc(userProfileRef);
          const userProfile = userProfileSnap.exists() ? userProfileSnap.data() : {};
  
          const enhancedUser = {
            ...firebaseUser,
            role: decodedToken?.role || userProfile.role || 'public',
            products: normalizeProducts(decodedToken?.products || userProfile.products),
            customClaims: decodedToken,
            teams: userProfile.teams || {},
            teamGroups: userProfile.teamGroups || {},
            name: userProfile.name,
            department: userProfile.department,
            lastUpdated: userProfile.lastUpdated
          };
  
          setUser(enhancedUser);

          // Track once per browser session. sessionStorage is cleared when the tab closes,
          // so this fires on every new session whether the user typed credentials or
          // their Firebase session was restored from localStorage.
          const sessionKey = `loginTracked_${firebaseUser.uid}`;
          if (!sessionStorage.getItem(sessionKey)) {
            sessionStorage.setItem(sessionKey, 'true');
            setTimeout(async () => {
              try {
                await mailchimpService.trackLogin(firebaseUser.email);
              } catch (err) {
                console.warn('Login tracking failed:', err);
              }
            }, 0);
          }

          // SessionWarningDialog (mounted in App.js) handles the warning UI;
          // the expire callback here is the authoritative 1-hour forced logout.
          tokenManager.startSessionTimers(
            () => {},
            handleSessionExpire
          );
          
          // Set up token listener
          const unsubscribeToken = tokenManager.addListener((tokenInfo) => {
            if (tokenInfo) {
              setUser(current => ({
                ...current,
                role: tokenInfo.claims?.role || current.role,
                products: tokenInfo.claims?.products
                  ? normalizeProducts(tokenInfo.claims.products)
                  : current.products,
                customClaims: tokenInfo.claims
              }));
            }
          });
          
          return () => unsubscribeToken();
        } else {
          tokenManager.clearSessionTimers();
          setUser(null);
        }
      } catch (error) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    });
  
    return () => unsubscribe();
  }, [auth, db, handleSessionExpire]);

  const login = async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      const userProfileRef = doc(db, 'user_profiles', result.user.uid);
      const userProfileSnap = await getDoc(userProfileRef);
      const userProfile = userProfileSnap.exists() ? userProfileSnap.data() : {};
      
      const token = await result.user.getIdToken();
      const decodedToken = JSON.parse(atob(token.split('.')[1]));
  
      // Note: We use the base team ID initially since team data might not be loaded yet
      districtCache.prefetchDistrictData('base').catch(() => {});
  
      return {
        user: result.user,
        profile: userProfile,
        token: token,
        claims: decodedToken
      };
    } catch (error) {
      switch (error.code) {
        case 'auth/invalid-credential':
        case 'auth/wrong-password':
        case 'auth/user-not-found':
          throw new Error('Invalid email or password. Please try again.');
        
        case 'auth/too-many-requests':
          throw new Error('Too many login attempts. Please try again later or reset your password.');
        
        case 'auth/user-disabled':
          throw new Error(SUSPENDED_ACCOUNT_MESSAGE);
          
        case 'auth/invalid-email':
          throw new Error('Please enter a valid email address.');
          
        default:
          throw new Error('An error occurred during sign in. Please try again.');
      }
    }
  };

  const logout = async () => {
    try {
      tokenManager.clearSessionTimers();
      try { await apiClient.post('/logout'); } catch (_) {}
      await signOut(auth);
      clearActiveProduct();
      
      try {
        await districtCache.clearAllCache();
      } catch (_) {}
      
      const keysToKeep = new Set(['analyticsConsent', 'hasExplicitConsent']);
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!keysToKeep.has(key)) {
          localStorage.removeItem(key);
        }
      }
    } catch (error) {
      throw error;
    }
  };

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      if (error.code === 'auth/user-not-found') {
        throw new Error('No account found with this email address');
      }
      throw error;
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    resetPassword
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading ? children : (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh' 
        }}>
          <CircularProgress />
        </Box>
      )}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
